/* InteractiveProps — SVG renderers for each "prop" element type.
 *
 * All props render as an SVG <g> inside the whiteboard canvas. They can be
 * dragged (via the parent's onElementDown) and RESIZED via a handle at the
 * bottom-right corner. Each prop consumes standard element fields
 * (x, y, width, height, color, label) plus an optional `state` object for
 * behaviour (e.g. beaker fill level, circuit closed).
 *
 * Prop → svg mapping. Selected props get a yellow outline.
 */
import React from "react";

// Resize handle at the bottom-right corner — emits onResizeStart(el, ptr)
function ResizeHandle({ el, selected, onResizeStart }) {
  if (!selected) return null;
  return (
    <rect
      x={el.x + el.width - 8}
      y={el.y + el.height - 8}
      width={12} height={12}
      fill="#FFB400" stroke="#0A0C10" strokeWidth={1}
      style={{ cursor: "nwse-resize" }}
      data-testid={`prop-resize-${el.id}`}
      onMouseDown={(e) => { e.stopPropagation(); onResizeStart(el, e); }}
    />
  );
}

// Toggle button drawn as a small pill on the prop
function ToggleBtn({ x, y, on, label, onClick }) {
  return (
    <g onMouseDown={(e) => { e.stopPropagation(); onClick(e); }} style={{ cursor: "pointer" }}>
      <rect x={x} y={y} width={50} height={18} rx={9} fill={on ? "#10B981" : "#6B7280"} />
      <text x={x + 25} y={y + 13} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">{label}</text>
    </g>
  );
}

export function renderProp(el, ctx) {
  const { selected, onElementDown, onResizeStart, onStateChange, onDelete } = ctx;
  const commonG = { onMouseDown: (e) => onElementDown(e, el), style: { cursor: "grab" }, "data-testid": `wb-el-${el.id}` };
  const outline = selected ? (
    <rect x={el.x - 2} y={el.y - 2} width={el.width + 4} height={el.height + 4} fill="none" stroke="#FFB400" strokeWidth={2} strokeDasharray="6 4" pointerEvents="none" />
  ) : null;

  const label = el.label ? (
    <text x={el.x + el.width / 2} y={el.y + el.height + 16} textAnchor="middle" fontSize="12" fontWeight="600" fill="#0A0C10" pointerEvents="none">{el.label}</text>
  ) : null;

  switch (el.propType) {
    case "scale":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgRuler x={el.x} y={el.y} width={el.width} height={el.height} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "graph-paper":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgGraphPaper x={el.x} y={el.y} width={el.width} height={el.height} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "beaker":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgBeaker el={el} onStateChange={onStateChange} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "test-tube":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgTestTube el={el} onStateChange={onStateChange} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "burner":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgBurner el={el} />
          <ToggleBtn x={el.x + el.width - 56} y={el.y + el.height - 22} on={el.state?.on}
            label={el.state?.on ? "OFF" : "ON"}
            onClick={() => onStateChange(el.id, { on: !el.state?.on })}
          />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "circuit-bulb":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgCircuitBulb el={el} />
          <ToggleBtn x={el.x + el.width - 56} y={el.y + el.height - 22} on={el.state?.closed}
            label={el.state?.closed ? "OPEN" : "CLOSE"}
            onClick={() => onStateChange(el.id, { closed: !el.state?.closed })}
          />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "magnet":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgMagnet el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "lens":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgLens el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "pendulum":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgPendulum el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "wave":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgWave el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "force-diagram":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgForceDiagram el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "molecule-h2o":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgMoleculeH2O el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "periodic-snippet":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgPeriodicSnippet el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "pipette":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgPipette el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "cell":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgCell el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "dna":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgDNA el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "plant":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgPlant el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "heart":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgHeart el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "globe":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgGlobe el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "compass-rose":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgCompassRose el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "speech":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSpeech el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "quote":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgQuote el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "dice":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgDice el={el} onRoll={() => onStateChange(el.id, { face: 1 + Math.floor(Math.random() * 6) })} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "abacus":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgAbacus el={el} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "fraction-bar":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgFractionBar el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-projectile":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimProjectile el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-ph":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimPh el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-grapher":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimGrapher el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-mitosis":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimMitosis el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-wave-interference":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimWaveInterference el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-states-matter":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimStatesMatter el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "sim-punnett":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSimPunnett el={el} onStateChange={(patch) => onStateChange(el.id, patch)} />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    case "urdu-tracing":
      return (<g key={el.id} {...commonG}>{outline}<SvgUrduTracing el={el} onStateChange={(patch) => onStateChange(el.id, patch)} /><ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />{label}</g>);
    case "urdu-couplet":
      return (<g key={el.id} {...commonG}>{outline}<SvgUrduCouplet el={el} /><ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />{label}</g>);
    case "urdu-alphabet":
      return (<g key={el.id} {...commonG}>{outline}<SvgUrduAlphabet el={el} /><ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />{label}</g>);
    case "urdu-rtl-text":
      return (<g key={el.id} {...commonG}>{outline}<SvgUrduRTLText el={el} onStateChange={(patch) => onStateChange(el.id, patch)} /><ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />{label}</g>);
    case "skill-widget":
      return (
        <g key={el.id} {...commonG}>
          {outline}
          <SvgSkillWidget
            el={el}
            onStateChange={(patch) => onStateChange(el.id, patch)}
            onDelete={() => onDelete && onDelete(el.id)}
          />
          <ResizeHandle el={el} selected={selected} onResizeStart={onResizeStart} />
          {label}
        </g>
      );
    default:
      return null;
  }
}

// ─────────────── Individual prop renderers ───────────────
function SvgRuler({ x, y, width, height }) {
  const ticks = [];
  const total = 30;
  const step = width / total;
  for (let i = 0; i <= total; i++) {
    const long = i % 5 === 0;
    ticks.push(<line key={i} x1={x + i * step} y1={y} x2={x + i * step} y2={y + (long ? height * 0.55 : height * 0.28)} stroke="#0A0C10" strokeWidth={long ? 1.4 : 0.9} />);
    if (long) ticks.push(<text key={"n" + i} x={x + i * step} y={y + height - 8} fontSize={11} textAnchor="middle" fill="#0A0C10">{i}</text>);
  }
  return (
    <>
      <rect x={x} y={y} width={width} height={height} rx={4} fill="rgba(255,214,0,0.8)" stroke="#0A0C10" strokeWidth={1.5} />
      {ticks}
    </>
  );
}

function SvgGraphPaper({ x, y, width, height }) {
  const step = Math.min(width, height) / 12;
  const lines = [];
  for (let i = 0; i * step <= width; i++) lines.push(<line key={`vx${i}`} x1={x + i * step} y1={y} x2={x + i * step} y2={y + height} stroke="rgba(10,12,16,0.2)" strokeWidth={0.6} />);
  for (let i = 0; i * step <= height; i++) lines.push(<line key={`hy${i}`} x1={x} y1={y + i * step} x2={x + width} y2={y + i * step} stroke="rgba(10,12,16,0.2)" strokeWidth={0.6} />);
  return (
    <>
      <rect x={x} y={y} width={width} height={height} fill="white" stroke="#0A0C10" strokeWidth={1.5} />
      {lines}
      <line x1={x} y1={y + height / 2} x2={x + width} y2={y + height / 2} stroke="#0A0C10" strokeWidth={1.2} />
      <line x1={x + width / 2} y1={y} x2={x + width / 2} y2={y + height} stroke="#0A0C10" strokeWidth={1.2} />
    </>
  );
}

function SvgBeaker({ el, onStateChange }) {
  const fill = Math.max(0, Math.min(1, el.state?.fill ?? 0.5));
  const tint = el.state?.tint || "#60A5FA";
  const liquidH = fill * (el.height - 40);
  return (
    <>
      <rect x={el.x + el.width * 0.1} y={el.y + 20} width={el.width * 0.8} height={el.height - 20} rx={4} fill="rgba(191,219,254,0.3)" stroke="#0A0C10" strokeWidth={2} />
      {/* Liquid */}
      <rect x={el.x + el.width * 0.1 + 3} y={el.y + el.height - liquidH - 3} width={el.width * 0.8 - 6} height={liquidH} fill={tint} opacity={0.7} />
      {/* Spout */}
      <path d={`M ${el.x + el.width * 0.1} ${el.y + 24} Q ${el.x} ${el.y + 12} ${el.x + el.width * 0.02} ${el.y + 4}`} fill="none" stroke="#0A0C10" strokeWidth={2} />
      {/* Level indicators */}
      {[0.25, 0.5, 0.75].map((p) => (
        <line key={p} x1={el.x + el.width * 0.1} y1={el.y + el.height - p * (el.height - 40)} x2={el.x + el.width * 0.2} y2={el.y + el.height - p * (el.height - 40)} stroke="#0A0C10" strokeWidth={1} />
      ))}
      {/* Fill controls */}
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange(el.id, { fill: Math.max(0, fill - 0.1) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + 16} cy={el.y + el.height - 16} r={10} fill="#EF4444" />
        <text x={el.x + 16} y={el.y + el.height - 12} textAnchor="middle" fontSize={14} fill="#fff" fontWeight={700}>−</text>
      </g>
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange(el.id, { fill: Math.min(1, fill + 0.1) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + el.width - 16} cy={el.y + el.height - 16} r={10} fill="#10B981" />
        <text x={el.x + el.width - 16} y={el.y + el.height - 12} textAnchor="middle" fontSize={14} fill="#fff" fontWeight={700}>+</text>
      </g>
    </>
  );
}

function SvgTestTube({ el, onStateChange }) {
  const fill = Math.max(0, Math.min(1, el.state?.fill ?? 0.4));
  const tint = el.state?.tint || "#A78BFA";
  const tubeH = el.height - 40;
  const tubeW = el.width * 0.7;
  const cx = el.x + el.width / 2;
  return (
    <>
      <rect x={cx - tubeW / 2} y={el.y + 12} width={tubeW} height={tubeH - tubeW / 2} fill="rgba(255,255,255,0.5)" stroke="#0A0C10" strokeWidth={2} />
      <path d={`M ${cx - tubeW / 2} ${el.y + tubeH - tubeW / 2 + 12} A ${tubeW / 2} ${tubeW / 2} 0 0 0 ${cx + tubeW / 2} ${el.y + tubeH - tubeW / 2 + 12}`} fill="rgba(255,255,255,0.5)" stroke="#0A0C10" strokeWidth={2} />
      <path d={`M ${cx - tubeW / 2 + 3} ${el.y + tubeH - tubeW / 2 * fill * 2 + 12} L ${cx - tubeW / 2 + 3} ${el.y + tubeH - tubeW / 2 + 12} A ${tubeW / 2 - 3} ${tubeW / 2 - 3} 0 0 0 ${cx + tubeW / 2 - 3} ${el.y + tubeH - tubeW / 2 + 12} L ${cx + tubeW / 2 - 3} ${el.y + tubeH - tubeW / 2 * fill * 2 + 12} Z`} fill={tint} opacity={0.75} />
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange(el.id, { fill: Math.max(0, fill - 0.1) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + 12} cy={el.y + el.height - 12} r={9} fill="#EF4444" />
        <text x={el.x + 12} y={el.y + el.height - 8} textAnchor="middle" fontSize={13} fill="#fff" fontWeight={700}>−</text>
      </g>
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange(el.id, { fill: Math.min(1, fill + 0.1) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + el.width - 12} cy={el.y + el.height - 12} r={9} fill="#10B981" />
        <text x={el.x + el.width - 12} y={el.y + el.height - 8} textAnchor="middle" fontSize={13} fill="#fff" fontWeight={700}>+</text>
      </g>
    </>
  );
}

function SvgBurner({ el }) {
  const on = el.state?.on;
  const cx = el.x + el.width / 2;
  return (
    <>
      <rect x={cx - 25} y={el.y + el.height - 60} width={50} height={40} fill="#4B5563" stroke="#0A0C10" strokeWidth={1.5} />
      <rect x={cx - 15} y={el.y + el.height - 90} width={30} height={30} fill="#6B7280" stroke="#0A0C10" strokeWidth={1.5} />
      {on && (
        <>
          <path d={`M ${cx - 12} ${el.y + el.height - 90} Q ${cx} ${el.y + 20} ${cx + 12} ${el.y + el.height - 90} Z`} fill="url(#flame-grad)" opacity={0.9}>
            <animate attributeName="opacity" values="0.7;1;0.7" dur="0.6s" repeatCount="indefinite" />
          </path>
          <path d={`M ${cx - 6} ${el.y + el.height - 100} Q ${cx} ${el.y + 40} ${cx + 6} ${el.y + el.height - 100} Z`} fill="#FEF08A" opacity={0.8} />
        </>
      )}
      <defs>
        <linearGradient id="flame-grad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#DC2626" />
          <stop offset="0.5" stopColor="#F59E0B" />
          <stop offset="1" stopColor="#FEF3C7" />
        </linearGradient>
      </defs>
    </>
  );
}

function SvgCircuitBulb({ el }) {
  const on = el.state?.closed;
  const midY = el.y + el.height / 2;
  return (
    <>
      {/* wires */}
      <rect x={el.x + 20} y={midY - 30} width={40} height={60} fill="#0A0C10" />
      <text x={el.x + 40} y={midY + 6} textAnchor="middle" fontSize={12} fill="#FFD600" fontWeight={700}>BAT</text>
      <line x1={el.x + 60} y1={midY} x2={el.x + el.width - 90} y2={midY} stroke="#0A0C10" strokeWidth={3} />
      {/* Bulb */}
      <circle cx={el.x + el.width - 60} cy={midY} r={26} fill={on ? "#FEF08A" : "#E5E7EB"} stroke="#0A0C10" strokeWidth={2}>
        {on && <animate attributeName="opacity" values="0.7;1;0.7" dur="1.2s" repeatCount="indefinite" />}
      </circle>
      <line x1={el.x + el.width - 60} y1={midY + 20} x2={el.x + el.width - 60} y2={midY + 40} stroke="#0A0C10" strokeWidth={2} />
      {on && [-1, 0, 1].map((i) => (
        <line key={i} x1={el.x + el.width - 60 + i * 20} y1={midY - 40} x2={el.x + el.width - 60 + i * 30} y2={midY - 55} stroke="#F59E0B" strokeWidth={2} />
      ))}
      {/* Return wire */}
      <path d={`M ${el.x + el.width - 60} ${midY + 40} L ${el.x + el.width - 20} ${midY + 40} L ${el.x + el.width - 20} ${midY + 60} L ${el.x + 20} ${midY + 60} L ${el.x + 20} ${midY + 30}`} fill="none" stroke="#0A0C10" strokeWidth={3} />
      {!on && (
        <line x1={el.x + el.width / 2 - 20} y1={midY - 6} x2={el.x + el.width / 2 + 20} y2={midY + 6} stroke="#EF4444" strokeWidth={3} />
      )}
    </>
  );
}

function SvgMagnet({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const barW = el.width * 0.6;
  const barH = el.height * 0.28;
  const lines = [];
  for (let i = 0; i < 4; i++) {
    const off = 20 + i * 14;
    lines.push(<path key={i} d={`M ${cx - barW / 2 - 4} ${cy} C ${cx - off} ${cy - off * 1.4} ${cx + off} ${cy - off * 1.4} ${cx + barW / 2 + 4} ${cy}`} fill="none" stroke="rgba(220,38,38,0.5)" strokeWidth={1.5} />);
    lines.push(<path key={"b" + i} d={`M ${cx - barW / 2 - 4} ${cy} C ${cx - off} ${cy + off * 1.4} ${cx + off} ${cy + off * 1.4} ${cx + barW / 2 + 4} ${cy}`} fill="none" stroke="rgba(59,130,246,0.5)" strokeWidth={1.5} />);
  }
  return (
    <>
      {lines}
      <rect x={cx - barW / 2} y={cy - barH / 2} width={barW / 2} height={barH} fill="#DC2626" stroke="#0A0C10" strokeWidth={2} />
      <rect x={cx} y={cy - barH / 2} width={barW / 2} height={barH} fill="#3B82F6" stroke="#0A0C10" strokeWidth={2} />
      <text x={cx - barW / 4} y={cy + 6} textAnchor="middle" fontSize={20} fill="#fff" fontWeight={800}>N</text>
      <text x={cx + barW / 4} y={cy + 6} textAnchor="middle" fontSize={20} fill="#fff" fontWeight={800}>S</text>
    </>
  );
}

function SvgLens({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const rx = el.width * 0.12;
  const ry = el.height * 0.42;
  return (
    <>
      <line x1={el.x} y1={cy} x2={el.x + el.width} y2={cy} stroke="#0A0C10" strokeWidth={1} strokeDasharray="4 4" />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="rgba(14,165,233,0.25)" stroke="#0EA5E9" strokeWidth={2} />
      {/* Ray in */}
      <line x1={el.x + 4} y1={cy - 30} x2={cx - rx} y2={cy - 30} stroke="#F59E0B" strokeWidth={2} markerEnd="url(#ah)" />
      {/* Ray out (bent to focal) */}
      <line x1={cx - rx} y1={cy - 30} x2={cx + rx} y2={cy + 12} stroke="#F59E0B" strokeWidth={2} />
      <line x1={cx + rx} y1={cy + 12} x2={el.x + el.width - 4} y2={cy + 40} stroke="#F59E0B" strokeWidth={2} markerEnd="url(#ah)" />
      <text x={el.x + el.width - 10} y={cy - 10} textAnchor="end" fontSize={11} fill="#0EA5E9">focal</text>
    </>
  );
}

function SvgPendulum({ el }) {
  const swinging = el.state?.swinging !== false;
  const cx = el.x + el.width / 2;
  return (
    <g>
      <rect x={cx - 30} y={el.y + 10} width={60} height={8} fill="#0A0C10" />
      <g style={{ transformOrigin: `${cx}px ${el.y + 14}px` }}>
        {swinging && (
          <animateTransform attributeName="transform" attributeType="XML" type="rotate"
            values={`-25 ${cx} ${el.y + 14}; 25 ${cx} ${el.y + 14}; -25 ${cx} ${el.y + 14}`}
            dur="2.4s" repeatCount="indefinite" />
        )}
        <line x1={cx} y1={el.y + 18} x2={cx} y2={el.y + el.height - 30} stroke="#0A0C10" strokeWidth={2} />
        <circle cx={cx} cy={el.y + el.height - 22} r={20} fill="#7C3AED" stroke="#0A0C10" strokeWidth={1.5} />
      </g>
    </g>
  );
}

function SvgWave({ el }) {
  const freq = el.state?.freq || 2;
  const cy = el.y + el.height / 2;
  const amp = el.height * 0.35;
  let d = `M ${el.x} ${cy}`;
  for (let i = 0; i <= el.width; i += 4) {
    d += ` L ${el.x + i} ${cy + Math.sin((i / el.width) * Math.PI * 2 * freq) * amp}`;
  }
  return (
    <>
      <line x1={el.x} y1={cy} x2={el.x + el.width} y2={cy} stroke="#0A0C10" strokeWidth={0.8} strokeDasharray="3 3" />
      <path d={d} fill="none" stroke="#10B981" strokeWidth={2.5} />
    </>
  );
}

function SvgForceDiagram({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  return (
    <>
      <rect x={cx - 40} y={cy - 40} width={80} height={80} fill="#F59E0B" stroke="#0A0C10" strokeWidth={2} />
      <line x1={cx} y1={cy} x2={cx + 90} y2={cy} stroke="#EF4444" strokeWidth={3} markerEnd="url(#ah)" />
      <text x={cx + 60} y={cy - 6} fontSize={12} fill="#EF4444" fontWeight={700}>F</text>
      <line x1={cx} y1={cy} x2={cx} y2={cy + 80} stroke="#0EA5E9" strokeWidth={3} markerEnd="url(#ah)" />
      <text x={cx + 6} y={cy + 60} fontSize={12} fill="#0EA5E9" fontWeight={700}>g</text>
      <line x1={cx} y1={cy} x2={cx} y2={cy - 80} stroke="#10B981" strokeWidth={3} markerEnd="url(#ah)" />
      <text x={cx + 6} y={cy - 60} fontSize={12} fill="#10B981" fontWeight={700}>N</text>
    </>
  );
}

function SvgMoleculeH2O({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  return (
    <>
      <circle cx={cx} cy={cy - 30} r={35} fill="#EF4444" stroke="#0A0C10" strokeWidth={2} />
      <text x={cx} y={cy - 22} textAnchor="middle" fontSize={22} fill="#fff" fontWeight={800}>O</text>
      <circle cx={cx - 50} cy={cy + 40} r={22} fill="#0EA5E9" stroke="#0A0C10" strokeWidth={2} />
      <text x={cx - 50} y={cy + 47} textAnchor="middle" fontSize={16} fill="#fff" fontWeight={800}>H</text>
      <circle cx={cx + 50} cy={cy + 40} r={22} fill="#0EA5E9" stroke="#0A0C10" strokeWidth={2} />
      <text x={cx + 50} y={cy + 47} textAnchor="middle" fontSize={16} fill="#fff" fontWeight={800}>H</text>
      <line x1={cx - 15} y1={cy - 10} x2={cx - 35} y2={cy + 25} stroke="#0A0C10" strokeWidth={2.5} />
      <line x1={cx + 15} y1={cy - 10} x2={cx + 35} y2={cy + 25} stroke="#0A0C10" strokeWidth={2.5} />
    </>
  );
}

function SvgPeriodicSnippet({ el }) {
  const elements = [
    { s: "H", n: 1, c: "#F59E0B" }, { s: "He", n: 2, c: "#EF4444" },
    { s: "Li", n: 3, c: "#DC2626" }, { s: "Be", n: 4, c: "#FBBF24" },
    { s: "B", n: 5, c: "#84CC16" }, { s: "C", n: 6, c: "#4B5563" },
    { s: "N", n: 7, c: "#3B82F6" }, { s: "O", n: 8, c: "#0EA5E9" },
    { s: "F", n: 9, c: "#22D3EE" }, { s: "Ne", n: 10, c: "#EF4444" },
  ];
  const cellW = el.width / 5;
  const cellH = el.height / 2;
  return (
    <>
      {elements.map((e, i) => {
        const col = i % 5;
        const row = Math.floor(i / 5);
        return (
          <g key={e.s}>
            <rect x={el.x + col * cellW + 2} y={el.y + row * cellH + 2} width={cellW - 4} height={cellH - 4} fill={e.c} stroke="#0A0C10" strokeWidth={1} />
            <text x={el.x + col * cellW + 8} y={el.y + row * cellH + 20} fontSize={11} fill="#fff" fontWeight={700}>{e.n}</text>
            <text x={el.x + col * cellW + cellW / 2} y={el.y + row * cellH + cellH / 2 + 6} textAnchor="middle" fontSize={22} fill="#fff" fontWeight={800}>{e.s}</text>
          </g>
        );
      })}
    </>
  );
}

function SvgPipette({ el }) {
  const cx = el.x + el.width / 2;
  return (
    <>
      <rect x={cx - 6} y={el.y + 8} width={12} height={el.height - 40} fill="#DBEAFE" stroke="#0A0C10" strokeWidth={2} />
      <path d={`M ${cx - 6} ${el.y + el.height - 32} L ${cx} ${el.y + el.height - 6} L ${cx + 6} ${el.y + el.height - 32} Z`} fill="#DBEAFE" stroke="#0A0C10" strokeWidth={2} />
      <ellipse cx={cx} cy={el.y + 8} rx={12} ry={8} fill="#DC2626" stroke="#0A0C10" strokeWidth={1.5} />
    </>
  );
}

function SvgCell({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  return (
    <>
      <ellipse cx={cx} cy={cy} rx={el.width * 0.42} ry={el.height * 0.42} fill="rgba(34,197,94,0.2)" stroke="#22C55E" strokeWidth={2} />
      <ellipse cx={cx - 20} cy={cy + 10} rx={30} ry={26} fill="rgba(124,58,237,0.35)" stroke="#7C3AED" strokeWidth={1.8} />
      <text x={cx - 20} y={cy + 14} textAnchor="middle" fontSize={11} fill="#0A0C10">Nucleus</text>
      <ellipse cx={cx + 40} cy={cy - 30} rx={16} ry={10} fill="#0EA5E9" opacity={0.6} />
      <ellipse cx={cx + 50} cy={cy + 30} rx={20} ry={12} fill="#F59E0B" opacity={0.6} />
    </>
  );
}

function SvgDNA({ el }) {
  const cx = el.x + el.width / 2;
  const pts = [];
  const rungs = [];
  for (let i = 0; i <= 20; i++) {
    const t = i / 20;
    const y = el.y + t * el.height;
    const off = Math.sin(t * Math.PI * 4) * (el.width * 0.3);
    pts.push([cx - off, y]);
    if (i % 2 === 0) rungs.push([cx - off, cx + off, y]);
  }
  const otherPts = pts.map(([px, y]) => [2 * cx - px, y]);
  return (
    <>
      <path d={pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ")} fill="none" stroke="#7C3AED" strokeWidth={3} />
      <path d={otherPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ")} fill="none" stroke="#DC2626" strokeWidth={3} />
      {rungs.map((r, i) => <line key={i} x1={r[0]} y1={r[2]} x2={r[1]} y2={r[2]} stroke="#0A0C10" strokeWidth={1.5} />)}
    </>
  );
}

function SvgPlant({ el }) {
  const cx = el.x + el.width / 2;
  return (
    <>
      <line x1={cx} y1={el.y + 40} x2={cx} y2={el.y + el.height - 30} stroke="#10B981" strokeWidth={5} />
      <path d={`M ${cx - 40} ${el.y + 80} Q ${cx - 60} ${el.y + 60} ${cx} ${el.y + 70}`} fill="#22C55E" stroke="#0A0C10" strokeWidth={1.5} />
      <path d={`M ${cx + 40} ${el.y + 110} Q ${cx + 70} ${el.y + 90} ${cx} ${el.y + 100}`} fill="#22C55E" stroke="#0A0C10" strokeWidth={1.5} />
      <circle cx={cx} cy={el.y + 40} r={22} fill="#F59E0B" stroke="#0A0C10" strokeWidth={2} />
      <ellipse cx={cx} cy={el.y + el.height - 12} rx={el.width * 0.35} ry={12} fill="#78350F" stroke="#0A0C10" strokeWidth={1.5} />
    </>
  );
}

function SvgHeart({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const scale = Math.min(el.width, el.height) / 320;
  const d = "M 0 40 C 0 -30 -80 -30 -80 30 C -80 90 0 130 0 170 C 0 130 80 90 80 30 C 80 -30 0 -30 0 40 Z";
  return (
    <g transform={`translate(${cx}, ${cy - 20}) scale(${scale})`}>
      {el.state?.beating ? (
        <path d={d} fill="#EF4444" stroke="#0A0C10" strokeWidth={4}>
          <animateTransform attributeName="transform" type="scale" values="1;1.08;1" dur="1s" repeatCount="indefinite" />
        </path>
      ) : (
        <path d={d} fill="#EF4444" stroke="#0A0C10" strokeWidth={4} />
      )}
    </g>
  );
}

function SvgGlobe({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const r = Math.min(el.width, el.height) * 0.42;
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="rgba(59,130,246,0.4)" stroke="#0EA5E9" strokeWidth={2} />
      <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.3} fill="none" stroke="#0A0C10" strokeWidth={1} strokeDasharray="3 3" />
      <ellipse cx={cx} cy={cy} rx={r * 0.3} ry={r} fill="none" stroke="#0A0C10" strokeWidth={1} strokeDasharray="3 3" />
      {/* Continents (rough blobs) */}
      <path d={`M ${cx - r * 0.4} ${cy - r * 0.3} q 20 -10 40 0 t 20 30 t -30 20 z`} fill="#22C55E" />
      <path d={`M ${cx + r * 0.2} ${cy + r * 0.1} q 10 -5 20 5 t 5 20 t -20 5 z`} fill="#22C55E" />
    </>
  );
}

function SvgCompassRose({ el }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const r = Math.min(el.width, el.height) * 0.4;
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="rgba(245,158,11,0.15)" stroke="#0A0C10" strokeWidth={1.5} />
      <polygon points={`${cx},${cy - r} ${cx + 8},${cy} ${cx},${cy + r} ${cx - 8},${cy}`} fill="#EF4444" stroke="#0A0C10" strokeWidth={1.5} />
      <polygon points={`${cx - r},${cy} ${cx},${cy - 8} ${cx + r},${cy} ${cx},${cy + 8}`} fill="#F59E0B" stroke="#0A0C10" strokeWidth={1.5} />
      <text x={cx} y={cy - r - 6} textAnchor="middle" fontSize={16} fontWeight={800} fill="#EF4444">N</text>
      <text x={cx + r + 12} y={cy + 4} textAnchor="start" fontSize={14} fontWeight={700} fill="#0A0C10">E</text>
      <text x={cx} y={cy + r + 18} textAnchor="middle" fontSize={14} fontWeight={700} fill="#0A0C10">S</text>
      <text x={cx - r - 12} y={cy + 4} textAnchor="end" fontSize={14} fontWeight={700} fill="#0A0C10">W</text>
    </>
  );
}

function SvgSpeech({ el }) {
  return (
    <>
      <path d={`M ${el.x} ${el.y + 20} Q ${el.x} ${el.y} ${el.x + 20} ${el.y} L ${el.x + el.width - 20} ${el.y} Q ${el.x + el.width} ${el.y} ${el.x + el.width} ${el.y + 20} L ${el.x + el.width} ${el.y + el.height - 40} Q ${el.x + el.width} ${el.y + el.height - 20} ${el.x + el.width - 20} ${el.y + el.height - 20} L ${el.x + 60} ${el.y + el.height - 20} L ${el.x + 40} ${el.y + el.height} L ${el.x + 50} ${el.y + el.height - 20} L ${el.x + 20} ${el.y + el.height - 20} Q ${el.x} ${el.y + el.height - 20} ${el.x} ${el.y + el.height - 40} Z`} fill="white" stroke={el.color} strokeWidth={2} />
      <text x={el.x + el.width / 2} y={el.y + el.height / 2 - 4} textAnchor="middle" fontSize={14} fontWeight={600} fill="#0A0C10">Say something…</text>
    </>
  );
}

function SvgQuote({ el }) {
  return (
    <>
      <text x={el.x + 8} y={el.y + 30} fontSize={40} fontWeight={800} fill={el.color}>&#8220;</text>
      <text x={el.x + el.width - 8} y={el.y + el.height - 6} textAnchor="end" fontSize={40} fontWeight={800} fill={el.color}>&#8221;</text>
      <text x={el.x + el.width / 2} y={el.y + el.height / 2 + 6} textAnchor="middle" fontSize={16} fontWeight={600} fill="#0A0C10">Quote goes here</text>
    </>
  );
}

function SvgDice({ el, onRoll }) {
  const face = el.state?.face || 6;
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const r = Math.min(el.width, el.height) * 0.08;
  const dotPositions = {
    1: [[0, 0]],
    2: [[-0.4, -0.4], [0.4, 0.4]],
    3: [[-0.4, -0.4], [0, 0], [0.4, 0.4]],
    4: [[-0.4, -0.4], [0.4, -0.4], [-0.4, 0.4], [0.4, 0.4]],
    5: [[-0.4, -0.4], [0.4, -0.4], [0, 0], [-0.4, 0.4], [0.4, 0.4]],
    6: [[-0.4, -0.4], [0.4, -0.4], [-0.4, 0], [0.4, 0], [-0.4, 0.4], [0.4, 0.4]],
  };
  return (
    <>
      <rect x={el.x + 4} y={el.y + 4} width={el.width - 8} height={el.height - 8} rx={12} fill="white" stroke="#0A0C10" strokeWidth={2} />
      {(dotPositions[face] || []).map(([dx, dy], i) => (
        <circle key={i} cx={cx + dx * el.width * 0.5} cy={cy + dy * el.height * 0.5} r={r} fill="#DC2626" />
      ))}
      <g onMouseDown={(e) => { e.stopPropagation(); onRoll(); }} style={{ cursor: "pointer" }}>
        <rect x={el.x + el.width - 32} y={el.y + 4} width={28} height={16} rx={4} fill="#0A0C10" />
        <text x={el.x + el.width - 18} y={el.y + 15} textAnchor="middle" fontSize={9} fill="#FFD600" fontWeight={700}>ROLL</text>
      </g>
    </>
  );
}

function SvgAbacus({ el }) {
  const rows = 5;
  const cols = 9;
  const cellW = (el.width - 20) / cols;
  const rowH = (el.height - 30) / rows;
  const beads = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      beads.push(<circle key={`${r}-${c}`} cx={el.x + 10 + c * cellW + cellW / 2} cy={el.y + 20 + r * rowH + rowH / 2} r={Math.min(cellW, rowH) * 0.32} fill={c < 4 ? "#F59E0B" : "#0A0C10"} stroke="#0A0C10" strokeWidth={1} />);
    }
  }
  return (
    <>
      <rect x={el.x + 4} y={el.y + 4} width={el.width - 8} height={el.height - 8} rx={8} fill="#78350F" stroke="#0A0C10" strokeWidth={2} />
      <rect x={el.x + 10} y={el.y + 15} width={el.width - 20} height={el.height - 25} fill="#FEF3C7" />
      {[...Array(rows)].map((_, r) => <line key={r} x1={el.x + 10} y1={el.y + 20 + r * rowH + rowH / 2} x2={el.x + el.width - 10} y2={el.y + 20 + r * rowH + rowH / 2} stroke="#0A0C10" strokeWidth={1} />)}
      {beads}
    </>
  );
}

function SvgFractionBar({ el, onStateChange }) {
  const parts = el.state?.parts || 4;
  const filled = el.state?.filled || 1;
  const cellW = (el.width - 60) / parts;
  const y0 = el.y + 10;
  const h = el.height - 40;
  const cells = [];
  for (let i = 0; i < parts; i++) {
    cells.push(
      <rect key={i} x={el.x + 30 + i * cellW} y={y0} width={cellW - 2} height={h}
        fill={i < filled ? el.color : "white"} stroke="#0A0C10" strokeWidth={1.5}
        onMouseDown={(e) => { e.stopPropagation(); onStateChange({ filled: i + 1 }); }} style={{ cursor: "pointer" }} />
    );
  }
  return (
    <>
      {cells}
      <text x={el.x + el.width / 2} y={el.y + el.height - 6} textAnchor="middle" fontSize={14} fontWeight={700} fill="#0A0C10">{filled} / {parts}</text>
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange({ parts: Math.max(1, parts - 1), filled: Math.min(filled, Math.max(1, parts - 1)) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + 14} cy={el.y + h / 2 + 10} r={10} fill="#EF4444" />
        <text x={el.x + 14} y={el.y + h / 2 + 14} textAnchor="middle" fontSize={14} fill="#fff" fontWeight={800}>−</text>
      </g>
      <g onMouseDown={(e) => { e.stopPropagation(); onStateChange({ parts: Math.min(12, parts + 1) }); }} style={{ cursor: "pointer" }}>
        <circle cx={el.x + el.width - 14} cy={el.y + h / 2 + 10} r={10} fill="#10B981" />
        <text x={el.x + el.width - 14} y={el.y + h / 2 + 14} textAnchor="middle" fontSize={14} fill="#fff" fontWeight={800}>+</text>
      </g>
    </>
  );
}

// ─────────────── Flagship simulations ───────────────

/** SvgSimProjectile — Interactive projectile motion.
 * State: { angle (deg), speed (m/s scaled), running, t (frames) }
 * User adjusts angle + speed via ± buttons, clicks FIRE, ball animates
 * along a parabolic path with visible trail.
 */
function SvgSimProjectile({ el, onStateChange }) {
  const angle = el.state?.angle ?? 45;
  const speed = el.state?.speed ?? 60;
  const running = !!el.state?.running;
  const t = el.state?.t ?? 0;

  React.useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      onStateChange({ t: (el.state?.t ?? 0) + 0.06 });
    }, 40);
    return () => clearInterval(id);
  }, [running, el.state?.t]);

  const g = 9.8;
  const rad = (angle * Math.PI) / 180;
  const vx = speed * Math.cos(rad);
  const vy = speed * Math.sin(rad);
  const scale = 0.5; // world → svg
  const groundY = el.y + el.height - 30;
  const originX = el.x + 40;
  // Compute trajectory sample points up to time t (or full range if not running)
  const totalFlightT = (2 * vy) / g;
  const flight = Math.min(t, totalFlightT);
  const trailPts = [];
  const steps = 60;
  for (let i = 0; i <= steps; i++) {
    const ti = (i / steps) * flight;
    const px = originX + vx * ti * scale;
    const py = groundY - (vy * ti - 0.5 * g * ti * ti) * scale;
    if (px > el.x + el.width - 10) break;
    trailPts.push([px, py]);
  }
  const currentX = trailPts.length ? trailPts[trailPts.length - 1][0] : originX;
  const currentY = trailPts.length ? trailPts[trailPts.length - 1][1] : groundY;

  // Peak + range (only when finished)
  const peak = (vy * vy) / (2 * g) * scale;
  const range = (vx * totalFlightT) * scale;

  return (
    <>
      {/* Background */}
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#F5F3FF" stroke="#7C3AED" strokeWidth={2} />
      {/* Ground */}
      <line x1={el.x + 10} y1={groundY} x2={el.x + el.width - 10} y2={groundY} stroke="#7C3AED" strokeWidth={2} />
      {/* Angle indicator */}
      <line x1={originX} y1={groundY} x2={originX + Math.cos(rad) * 60} y2={groundY - Math.sin(rad) * 60} stroke="#DC2626" strokeWidth={2} strokeDasharray="4 3" />
      {/* Trail */}
      {trailPts.length > 1 && (
        <path d={trailPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ")}
          fill="none" stroke="#7C3AED" strokeWidth={2} />
      )}
      {/* Ball */}
      <circle cx={currentX} cy={currentY} r={8} fill="#DC2626" stroke="#0A0C10" strokeWidth={1.5} />
      {/* Cannon */}
      <rect x={originX - 12} y={groundY - 4} width={24} height={12} fill="#0A0C10" />

      {/* Read-outs */}
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">Projectile Motion</text>
      <text x={el.x + 12} y={el.y + 40} fontSize={11} fill="#0A0C10">angle {Math.round(angle)}°   speed {Math.round(speed)}</text>
      <text x={el.x + 12} y={el.y + 56} fontSize={11} fill="#7C3AED">peak ≈ {peak.toFixed(0)}px    range ≈ {range.toFixed(0)}px</text>

      {/* Controls */}
      <PropButton x={el.x + el.width - 200} y={el.y + 10} label="-A" onClick={() => onStateChange({ angle: Math.max(0, angle - 5) })} />
      <PropButton x={el.x + el.width - 168} y={el.y + 10} label="+A" onClick={() => onStateChange({ angle: Math.min(90, angle + 5) })} />
      <PropButton x={el.x + el.width - 132} y={el.y + 10} label="-V" onClick={() => onStateChange({ speed: Math.max(10, speed - 5) })} />
      <PropButton x={el.x + el.width - 100} y={el.y + 10} label="+V" onClick={() => onStateChange({ speed: Math.min(140, speed + 5) })} />
      <PropButton x={el.x + el.width - 64} y={el.y + 10} label={running ? "STOP" : "FIRE"}
        onClick={() => onStateChange(running ? { running: false } : { running: true, t: 0 })}
        color={running ? "#EF4444" : "#10B981"} width={54} />
    </>
  );
}

/** SvgSimPh — pH scale simulator.
 * State: { ph } — user drags a knob to move pH 0..14, sample changes color
 * and shows a substance label based on the pH.
 */
function SvgSimPh({ el, onStateChange }) {
  const ph = Math.max(0, Math.min(14, el.state?.ph ?? 7));
  const trackY = el.y + 90;
  const trackX0 = el.x + 20;
  const trackW = el.width - 40;
  const knobX = trackX0 + (ph / 14) * trackW;
  // pH → color gradient (red-orange-yellow-green-blue-purple)
  const stops = [
    { p: 0, c: "#B91C1C" }, { p: 2, c: "#DC2626" }, { p: 4, c: "#F97316" },
    { p: 6, c: "#FACC15" }, { p: 7, c: "#22C55E" }, { p: 9, c: "#0EA5E9" },
    { p: 12, c: "#7C3AED" }, { p: 14, c: "#4B0082" },
  ];
  const findColor = (p) => {
    for (let i = 0; i < stops.length - 1; i++) {
      if (p >= stops[i].p && p <= stops[i + 1].p) return stops[i + 1].c;
    }
    return "#22C55E";
  };
  const sampleColor = findColor(ph);
  const substance = ph < 1.5 ? "Battery acid" :
                    ph < 2.5 ? "Stomach acid" :
                    ph < 4 ? "Lemon juice" :
                    ph < 5.5 ? "Coffee" :
                    ph < 6.5 ? "Milk" :
                    ph < 7.5 ? "Pure water" :
                    ph < 8.5 ? "Seawater" :
                    ph < 10 ? "Baking soda" :
                    ph < 12 ? "Ammonia" :
                    ph < 13 ? "Bleach" : "Drain cleaner";
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FEFCE8" stroke="#DC2626" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">pH Scale</text>
      {/* Track — colored gradient */}
      <defs>
        <linearGradient id={`ph-grad-${el.id}`} x1="0" y1="0" x2="1" y2="0">
          {stops.map((s) => <stop key={s.p} offset={s.p / 14} stopColor={s.c} />)}
        </linearGradient>
      </defs>
      <rect x={trackX0} y={trackY - 8} width={trackW} height={16} rx={8} fill={`url(#ph-grad-${el.id})`} stroke="#0A0C10" strokeWidth={1} />
      {[0, 2, 4, 6, 7, 8, 10, 12, 14].map((p) => (
        <g key={p}>
          <line x1={trackX0 + (p / 14) * trackW} y1={trackY + 10} x2={trackX0 + (p / 14) * trackW} y2={trackY + 16} stroke="#0A0C10" strokeWidth={1} />
          <text x={trackX0 + (p / 14) * trackW} y={trackY + 30} fontSize={10} textAnchor="middle" fill="#0A0C10">{p}</text>
        </g>
      ))}
      {/* Knob */}
      <circle cx={knobX} cy={trackY} r={12} fill="#0A0C10"
        onMouseDown={(e) => { e.stopPropagation(); }} style={{ cursor: "ew-resize" }} />
      <circle cx={knobX} cy={trackY} r={5} fill="#FFD600" pointerEvents="none" />
      {/* Sample beaker preview */}
      <rect x={el.x + el.width - 100} y={el.y + 50} width={70} height={110} rx={4} fill={sampleColor} opacity={0.85} stroke="#0A0C10" strokeWidth={2} />
      <text x={el.x + el.width - 65} y={el.y + 170} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0A0C10">{substance}</text>
      <text x={el.x + 12} y={el.y + 44} fontSize={20} fontWeight={800} fill="#0A0C10">pH = {ph.toFixed(1)}</text>
      <text x={el.x + 12} y={el.y + 64} fontSize={11} fill="#0A0C10">{ph < 6.5 ? "ACIDIC" : ph > 7.5 ? "BASIC" : "NEUTRAL"}</text>
      {/* ± knob controls */}
      <PropButton x={el.x + 12} y={el.y + el.height - 36} label="-" onClick={() => onStateChange({ ph: Math.max(0, +(ph - 0.5).toFixed(1)) })} width={28} />
      <PropButton x={el.x + 44} y={el.y + el.height - 36} label="+" onClick={() => onStateChange({ ph: Math.min(14, +(ph + 0.5).toFixed(1)) })} width={28} />
    </>
  );
}

/** SvgSimGrapher — Function grapher. Type y=f(x), plot live.
 * State: { fn, xMin, xMax }
 * Supports: x, +, -, *, /, ^, sin, cos, tan, sqrt, log, ln, abs, pi, e.
 */
function SvgSimGrapher({ el, onStateChange }) {
  const fn = el.state?.fn ?? "x^2/50";
  const xMin = el.state?.xMin ?? -10;
  const xMax = el.state?.xMax ?? 10;
  const [inputVal, setInputVal] = React.useState(fn);
  React.useEffect(() => setInputVal(fn), [fn]);

  // Safe function eval — allow only whitelisted tokens
  const parseFn = (expr) => {
    try {
      const cleaned = expr.replace(/\^/g, "**");
      const f = new Function("x", `with(Math){ return (${cleaned}); }`);
      return f;
    } catch { return null; }
  };
  const f = parseFn(fn);
  const plotX0 = el.x + 40;
  const plotY0 = el.y + 90;
  const plotW = el.width - 60;
  const plotH = el.height - 130;
  const cx = plotX0 + plotW / 2;
  const cy = plotY0 + plotH / 2;
  const scale = plotW / (xMax - xMin);
  // Sample the function
  const pts = [];
  let lastY = null;
  if (f) {
    for (let i = 0; i <= plotW; i++) {
      const xv = xMin + (i / plotW) * (xMax - xMin);
      let yv;
      try { yv = f(xv); } catch { yv = NaN; }
      if (!Number.isFinite(yv)) { lastY = null; continue; }
      const py = cy - yv * (plotH / 20); // 20-unit y range
      const px = plotX0 + i;
      if (py < plotY0 || py > plotY0 + plotH) { lastY = null; continue; }
      pts.push(lastY === null ? `M ${px} ${py}` : `L ${px} ${py}`);
      lastY = py;
    }
  }
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#F5F3FF" stroke="#7C3AED" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">Function Grapher · y = f(x)</text>
      {/* Grid */}
      {(() => {
        const lines = [];
        for (let i = 0; i <= 10; i++) {
          lines.push(<line key={`vx${i}`} x1={plotX0 + (i / 10) * plotW} y1={plotY0} x2={plotX0 + (i / 10) * plotW} y2={plotY0 + plotH} stroke="rgba(10,12,16,0.15)" strokeWidth={0.6} />);
          lines.push(<line key={`hy${i}`} x1={plotX0} y1={plotY0 + (i / 10) * plotH} x2={plotX0 + plotW} y2={plotY0 + (i / 10) * plotH} stroke="rgba(10,12,16,0.15)" strokeWidth={0.6} />);
        }
        return lines;
      })()}
      <rect x={plotX0} y={plotY0} width={plotW} height={plotH} fill="none" stroke="#0A0C10" strokeWidth={1.5} />
      {/* Axes */}
      <line x1={plotX0} y1={cy} x2={plotX0 + plotW} y2={cy} stroke="#0A0C10" strokeWidth={1.2} />
      <line x1={cx} y1={plotY0} x2={cx} y2={plotY0 + plotH} stroke="#0A0C10" strokeWidth={1.2} />
      {/* Function path */}
      {pts.length > 0 && <path d={pts.join(" ")} fill="none" stroke="#7C3AED" strokeWidth={2.5} />}
      {/* Input */}
      <foreignObject x={el.x + 12} y={el.y + el.height - 34} width={el.width - 24} height={26}>
        <div style={{ display: "flex", gap: 4 }}>
          <input value={inputVal} onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onStateChange({ fn: inputVal }); } }}
            placeholder="e.g. sin(x), x^2/10, 2*x+1"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ flex: 1, fontSize: 12, padding: "3px 8px", border: "1px solid #0A0C10", borderRadius: 4, background: "white", fontFamily: "ui-monospace, monospace", outline: "none" }} />
          <button onMouseDown={(e) => { e.stopPropagation(); onStateChange({ fn: inputVal }); }}
            style={{ fontSize: 11, padding: "3px 10px", background: "#7C3AED", color: "white", border: 0, borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>PLOT</button>
        </div>
      </foreignObject>
    </>
  );
}

function PropButton({ x, y, label, onClick, color = "#0A0C10", width = 28 }) {
  return (
    <g onMouseDown={(e) => { e.stopPropagation(); onClick(e); }} style={{ cursor: "pointer" }}>
      <rect x={x} y={y} width={width} height={18} rx={4} fill={color} />
      <text x={x + width / 2} y={y + 13} textAnchor="middle" fontSize={10} fontWeight={700} fill="#FFD600">{label}</text>
    </g>
  );
}


// ─────────────── Urdu / RTL props ───────────────
// Uses Noto Nastaliq Urdu when available, falls back to system nastaliq stack.
const URDU_FONT = '"Noto Nastaliq Urdu","Jameel Noori Nastaleeq","Alvi Nastaleeq","Times New Roman",serif';

function SvgUrduTracing({ el, onStateChange }) {
  const text = el.state?.text ?? "بسم اللہ";
  const [val, setVal] = React.useState(text);
  React.useEffect(() => setVal(text), [text]);
  // 3 tracing rows with dotted guide line
  const rowH = (el.height - 60) / 3;
  const rows = [];
  for (let i = 0; i < 3; i++) {
    const y = el.y + 30 + i * rowH;
    rows.push(
      <g key={i}>
        <line x1={el.x + 20} y1={y + rowH * 0.6} x2={el.x + el.width - 20} y2={y + rowH * 0.6}
          stroke="rgba(10,12,16,0.35)" strokeWidth={1} strokeDasharray="6 5" />
        <foreignObject x={el.x + 20} y={y} width={el.width - 40} height={rowH}>
          <div dir="rtl" style={{ fontFamily: URDU_FONT, fontSize: Math.max(28, rowH * 0.7), color: i === 0 ? "#0A0C10" : "rgba(10,12,16,0.18)", textAlign: "right", lineHeight: 1 }}>
            {val}
          </div>
        </foreignObject>
      </g>
    );
  }
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FEFCE8" stroke="#059669" strokeWidth={2} />
      {rows}
      <foreignObject x={el.x + 8} y={el.y + el.height - 28} width={el.width - 16} height={24}>
        <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => onStateChange({ text: val })}
          onMouseDown={(e) => e.stopPropagation()} dir="rtl"
          style={{ width: "100%", fontFamily: URDU_FONT, fontSize: 14, padding: "2px 8px", border: "1px solid #059669", borderRadius: 4, background: "white", outline: "none" }} />
      </foreignObject>
    </>
  );
}

function SvgUrduCouplet({ el }) {
  const line1 = el.state?.line1 || "";
  const line2 = el.state?.line2 || "";
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FFFBEA" stroke="#7C3AED" strokeWidth={2} />
      <foreignObject x={el.x + 20} y={el.y + 10} width={el.width - 40} height={el.height / 2 - 12}>
        <div dir="rtl" style={{ fontFamily: URDU_FONT, fontSize: Math.max(20, el.height * 0.16), color: "#0A0C10", textAlign: "right", lineHeight: 1.4 }}>{line1}</div>
      </foreignObject>
      <foreignObject x={el.x + 20} y={el.y + el.height / 2 + 4} width={el.width - 40} height={el.height / 2 - 12}>
        <div dir="rtl" style={{ fontFamily: URDU_FONT, fontSize: Math.max(20, el.height * 0.16), color: "#0A0C10", textAlign: "right", lineHeight: 1.4 }}>{line2}</div>
      </foreignObject>
    </>
  );
}

function SvgUrduAlphabet({ el }) {
  const letters = "ا ب پ ت ٹ ث ج چ ح خ د ڈ ذ ر ڑ ز ژ س ش ص ض ط ظ ع غ ف ق ک گ ل م ن و ہ ھ ء ی ے".split(" ");
  const cols = 10;
  const rows = Math.ceil(letters.length / cols);
  const cellW = (el.width - 20) / cols;
  const cellH = (el.height - 20) / rows;
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FEF2F2" stroke="#DC2626" strokeWidth={2} />
      {letters.map((ch, i) => {
        const c = i % cols;
        const r = Math.floor(i / cols);
        return (
          <foreignObject key={i} x={el.x + 10 + c * cellW} y={el.y + 10 + r * cellH} width={cellW} height={cellH}>
            <div dir="rtl" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: URDU_FONT, fontSize: Math.min(cellW, cellH) * 0.6, color: "#0A0C10" }}>{ch}</div>
          </foreignObject>
        );
      })}
    </>
  );
}

function SvgUrduRTLText({ el, onStateChange }) {
  const [val, setVal] = React.useState(el.state?.text || "");
  React.useEffect(() => { setVal(el.state?.text || ""); }, [el.state?.text]);
  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#EFF6FF" stroke="#0EA5E9" strokeWidth={2} />
      <foreignObject x={el.x + 10} y={el.y + 10} width={el.width - 20} height={el.height - 20}>
        <textarea value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => onStateChange({ text: val })}
          onMouseDown={(e) => e.stopPropagation()} dir="rtl"
          style={{ width: "100%", height: "100%", fontFamily: URDU_FONT, fontSize: Math.max(20, el.height * 0.35), color: "#0A0C10", border: "none", background: "transparent", resize: "none", outline: "none", textAlign: "right", lineHeight: 1.4 }} />
      </foreignObject>
    </>
  );
}



// ─────────────── Phase 5 flagship simulations ───────────────

/** SvgSimMitosis — animated cell division through 5 phases.
 * State: { phase: 0..4, running: bool }
 * Phases: 0 Interphase · 1 Prophase · 2 Metaphase · 3 Anaphase · 4 Telophase
 */
function SvgSimMitosis({ el, onStateChange }) {
  const phase = el.state?.phase ?? 0;
  const running = !!el.state?.running;
  const PHASE_NAMES = ["Interphase", "Prophase", "Metaphase", "Anaphase", "Telophase"];

  React.useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const p = (el.state?.phase ?? 0);
      onStateChange({ phase: (p + 1) % 5 });
    }, 1600);
    return () => clearInterval(id);
  }, [running, el.state?.phase]);

  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2 + 10;
  const r = Math.min(el.width, el.height) * 0.32;

  // Chromosome positions per phase — 4 chromosomes
  const chromo = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    if (phase === 0)      chromo.push({ cx: cx + Math.cos(a) * r * 0.4, cy: cy + Math.sin(a) * r * 0.4, tint: "#7C3AED" });
    else if (phase === 1) chromo.push({ cx: cx + Math.cos(a) * r * 0.5, cy: cy + Math.sin(a) * r * 0.5, tint: "#7C3AED" });
    else if (phase === 2) chromo.push({ cx: cx + (i - 1.5) * 22, cy: cy, tint: "#DC2626" });
    else if (phase === 3) chromo.push({ cx: cx + (i < 2 ? -60 : 60) + (i % 2) * 18, cy: cy, tint: "#DC2626" });
    else                  chromo.push({ cx: cx + (i < 2 ? -r * 0.9 : r * 0.9), cy: cy, tint: "#7C3AED" });
  }

  // Cell outline morphs during telophase (two cells)
  const showTwoCells = phase === 4;

  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#F0FDF4" stroke="#22C55E" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">Mitosis · {PHASE_NAMES[phase]}</text>
      <text x={el.x + 12} y={el.y + 38} fontSize={10} fill="#22C55E">phase {phase + 1} / 5</text>

      {/* Cell membrane */}
      {!showTwoCells && (
        <circle cx={cx} cy={cy} r={r} fill="rgba(34,197,94,0.08)" stroke="#22C55E" strokeWidth={2} />
      )}
      {showTwoCells && (
        <>
          <circle cx={cx - r * 0.75} cy={cy} r={r * 0.75} fill="rgba(34,197,94,0.08)" stroke="#22C55E" strokeWidth={2} />
          <circle cx={cx + r * 0.75} cy={cy} r={r * 0.75} fill="rgba(34,197,94,0.08)" stroke="#22C55E" strokeWidth={2} />
        </>
      )}

      {/* Nuclear envelope — visible pre-prophase and post-telophase */}
      {(phase === 0 || phase === 1 || phase === 4) && !showTwoCells && (
        <circle cx={cx} cy={cy} r={r * 0.55} fill="none" stroke="#7C3AED" strokeWidth={1.5} strokeDasharray={phase === 1 ? "3 3" : ""} />
      )}
      {showTwoCells && (
        <>
          <circle cx={cx - r * 0.75} cy={cy} r={r * 0.35} fill="none" stroke="#7C3AED" strokeWidth={1.5} />
          <circle cx={cx + r * 0.75} cy={cy} r={r * 0.35} fill="none" stroke="#7C3AED" strokeWidth={1.5} />
        </>
      )}

      {/* Spindle fibres in metaphase & anaphase */}
      {(phase === 2 || phase === 3) && (
        <>
          <line x1={el.x + el.width * 0.15} y1={cy} x2={el.x + el.width * 0.85} y2={cy} stroke="#F59E0B" strokeWidth={1} strokeDasharray="2 3" opacity={0.6} />
          {chromo.map((c, i) => (
            <line key={"sp" + i} x1={el.x + el.width * 0.15} y1={cy} x2={c.cx} y2={c.cy} stroke="#F59E0B" strokeWidth={0.8} opacity={0.5} />
          ))}
        </>
      )}

      {/* Chromosomes */}
      {chromo.map((c, i) => (
        <g key={i}>
          <ellipse cx={c.cx} cy={c.cy} rx={6} ry={12} fill={c.tint} stroke="#0A0C10" strokeWidth={1} />
          <ellipse cx={c.cx + 6} cy={c.cy} rx={6} ry={12} fill={c.tint} stroke="#0A0C10" strokeWidth={1} opacity={phase < 3 ? 1 : 0.9} />
        </g>
      ))}

      {/* Controls */}
      <PropButton x={el.x + el.width - 168} y={el.y + 10} label="◀" onClick={() => onStateChange({ phase: (phase + 4) % 5 })} />
      <PropButton x={el.x + el.width - 136} y={el.y + 10} label="▶" onClick={() => onStateChange({ phase: (phase + 1) % 5 })} />
      <PropButton x={el.x + el.width - 100} y={el.y + 10} label={running ? "PAUSE" : "PLAY"} width={64}
        onClick={() => onStateChange({ running: !running })} color={running ? "#EF4444" : "#10B981"} />
    </>
  );
}

/** SvgSimWaveInterference — two coherent wave sources; the visualization plots
 * superposition of two sine waves along a horizontal axis. State: { f1, f2, phase, t, running }
 */
function SvgSimWaveInterference({ el, onStateChange }) {
  const f1 = el.state?.f1 ?? 2;
  const f2 = el.state?.f2 ?? 2;
  const phase = el.state?.phase ?? 0;
  const t = el.state?.t ?? 0;
  const running = el.state?.running !== false;

  React.useEffect(() => {
    if (!running) return;
    const id = setInterval(() => onStateChange({ t: (el.state?.t ?? 0) + 0.06 }), 40);
    return () => clearInterval(id);
  }, [running, el.state?.t]);

  const midY = el.y + 100;
  const leftX = el.x + 30;
  const rightX = el.x + el.width - 30;
  const width = rightX - leftX;
  const steps = 120;
  const pts1 = [], pts2 = [], ptsSum = [];
  for (let i = 0; i <= steps; i++) {
    const px = leftX + (i / steps) * width;
    const u = (i / steps) * Math.PI * 4;
    const y1 = 22 * Math.sin(u * f1 - t * 2);
    const y2 = 22 * Math.sin(u * f2 - t * 2 + phase);
    pts1.push([px, midY - 60 + y1]);
    pts2.push([px, midY + 20 + y2]);
    ptsSum.push([px, midY + 100 + y1 + y2]);
  }
  const path = (pts) => pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const relPhase = ((phase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const kind = relPhase < 0.6 || relPhase > Math.PI * 2 - 0.6
    ? "constructive"
    : Math.abs(relPhase - Math.PI) < 0.6 ? "destructive" : "mixed";

  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#F0F9FF" stroke="#0EA5E9" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">Wave interference</text>
      <text x={el.x + 12} y={el.y + 38} fontSize={11} fill="#0EA5E9">
        f₁={f1.toFixed(1)}  f₂={f2.toFixed(1)}  Δφ={(relPhase / Math.PI).toFixed(2)}π  ·  {kind}
      </text>

      {/* Wave 1 */}
      <line x1={leftX} y1={midY - 60} x2={rightX} y2={midY - 60} stroke="#94A3B8" strokeWidth={0.5} />
      <path d={path(pts1)} stroke="#0EA5E9" strokeWidth={2} fill="none" />
      <text x={leftX + 4} y={midY - 68} fontSize={10} fill="#0EA5E9">A</text>

      {/* Wave 2 */}
      <line x1={leftX} y1={midY + 20} x2={rightX} y2={midY + 20} stroke="#94A3B8" strokeWidth={0.5} />
      <path d={path(pts2)} stroke="#7C3AED" strokeWidth={2} fill="none" />
      <text x={leftX + 4} y={midY + 12} fontSize={10} fill="#7C3AED">B</text>

      {/* Sum */}
      <line x1={leftX} y1={midY + 100} x2={rightX} y2={midY + 100} stroke="#94A3B8" strokeWidth={0.5} />
      <path d={path(ptsSum)} stroke="#DC2626" strokeWidth={2.5} fill="none" />
      <text x={leftX + 4} y={midY + 92} fontSize={10} fill="#DC2626">A + B</text>

      {/* Controls */}
      <PropButton x={el.x + el.width - 232} y={el.y + 10} label="-f₁" onClick={() => onStateChange({ f1: Math.max(0.5, f1 - 0.5) })} />
      <PropButton x={el.x + el.width - 200} y={el.y + 10} label="+f₁" onClick={() => onStateChange({ f1: Math.min(6, f1 + 0.5) })} />
      <PropButton x={el.x + el.width - 168} y={el.y + 10} label="-f₂" onClick={() => onStateChange({ f2: Math.max(0.5, f2 - 0.5) })} />
      <PropButton x={el.x + el.width - 136} y={el.y + 10} label="+f₂" onClick={() => onStateChange({ f2: Math.min(6, f2 + 0.5) })} />
      <PropButton x={el.x + el.width - 100} y={el.y + 10} label="-Δφ" onClick={() => onStateChange({ phase: phase - Math.PI / 4 })} />
      <PropButton x={el.x + el.width - 64}  y={el.y + 10} label="+Δφ" onClick={() => onStateChange({ phase: phase + Math.PI / 4 })} />
    </>
  );
}

/** SvgSimStatesMatter — 24 particles animate through solid / liquid / gas at
 * varying temperatures. State: { phase: 'solid'|'liquid'|'gas', temp }
 */
function SvgSimStatesMatter({ el, onStateChange }) {
  const phase = el.state?.phase || "solid";
  const temp = el.state?.temp ?? 20;
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 80);
    return () => clearInterval(id);
  }, []);

  // Auto-derive phase from temperature so the two controls feel connected.
  React.useEffect(() => {
    const derived = temp < 30 ? "solid" : temp < 80 ? "liquid" : "gas";
    if (derived !== phase) onStateChange({ phase: derived });
  }, [temp]);

  const boxX = el.x + 40;
  const boxY = el.y + 60;
  const boxW = el.width - 80;
  const boxH = el.height - 100;

  const N = 24;
  const cols = 6;
  const rows = 4;
  const gap = Math.min(boxW / cols, boxH / rows);

  const particles = [];
  for (let i = 0; i < N; i++) {
    const c = i % cols, r = Math.floor(i / cols);
    // solid: locked lattice with tiny vibration
    // liquid: slight lateral drift, denser near bottom
    // gas: freeform bounce inside the box
    const amp = phase === "solid" ? 1.2 : phase === "liquid" ? 6 : 16;
    const speed = phase === "solid" ? 8 : phase === "liquid" ? 3 : 1.4;
    const seed = i * 12.9898;
    const dx = Math.sin(tick / speed + seed) * amp;
    const dy = Math.cos(tick / speed + seed * 1.3) * amp;
    let cx = boxX + c * gap + gap / 2 + dx;
    let cy = phase === "gas"
      ? boxY + ((Math.sin(tick / 4 + seed) * 0.5 + 0.5) * (boxH - 12)) + 6
      : phase === "liquid"
        ? boxY + (r / rows) * boxH + gap * 0.7 + dy
        : boxY + r * gap + gap / 2 + dy;
    cx = Math.max(boxX + 6, Math.min(boxX + boxW - 6, cx));
    cy = Math.max(boxY + 6, Math.min(boxY + boxH - 6, cy));
    particles.push({ cx, cy });
  }

  const tint = phase === "solid" ? "#0EA5E9" : phase === "liquid" ? "#7C3AED" : "#EF4444";

  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FAF5FF" stroke="#7C3AED" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">States of matter · {phase.toUpperCase()}</text>
      <text x={el.x + 12} y={el.y + 38} fontSize={11} fill="#7C3AED">temp {temp}°C · particles vibrate more as energy rises</text>

      {/* Container */}
      <rect x={boxX} y={boxY} width={boxW} height={boxH} fill="rgba(124,58,237,0.05)" stroke="#7C3AED" strokeWidth={1.5} />

      {/* Particles */}
      {particles.map((p, i) => (
        <circle key={i} cx={p.cx} cy={p.cy} r={6} fill={tint} stroke="#0A0C10" strokeWidth={1} />
      ))}

      {/* Thermometer */}
      <rect x={el.x + el.width - 28} y={boxY} width={12} height={boxH} rx={6} fill="#F1F5F9" stroke="#0A0C10" strokeWidth={1} />
      <rect x={el.x + el.width - 28} y={boxY + boxH - (temp / 120) * boxH} width={12}
            height={(temp / 120) * boxH} fill={temp < 30 ? "#0EA5E9" : temp < 80 ? "#F59E0B" : "#EF4444"} />

      {/* Controls */}
      <PropButton x={el.x + el.width - 232} y={el.y + 10} label="-°" onClick={() => onStateChange({ temp: Math.max(-20, temp - 10) })} />
      <PropButton x={el.x + el.width - 200} y={el.y + 10} label="+°" onClick={() => onStateChange({ temp: Math.min(120, temp + 10) })} />
      <PropButton x={el.x + el.width - 168} y={el.y + 10} label="S" onClick={() => onStateChange({ phase: "solid", temp: 10 })} color="#0EA5E9" />
      <PropButton x={el.x + el.width - 136} y={el.y + 10} label="L" onClick={() => onStateChange({ phase: "liquid", temp: 50 })} color="#7C3AED" />
      <PropButton x={el.x + el.width - 104} y={el.y + 10} label="G" onClick={() => onStateChange({ phase: "gas", temp: 100 })} color="#EF4444" />
    </>
  );
}

/** SvgSimPunnett — genetics square from two parent genotypes.
 * State: { p1: "Aa", p2: "Aa" } — each is a 2-char string of alleles.
 * Uppercase = dominant, lowercase = recessive.
 */
function SvgSimPunnett({ el, onStateChange }) {
  const p1 = (el.state?.p1 || "Aa").slice(0, 2).padEnd(2, "a");
  const p2 = (el.state?.p2 || "Aa").slice(0, 2).padEnd(2, "a");

  const a1 = [p1[0], p1[1]];
  const a2 = [p2[0], p2[1]];

  // Build 4 offspring genotypes
  const cells = [];
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 2; c++) {
      const g = [a1[c], a2[r]].sort((x, y) => (x.toLowerCase() === y.toLowerCase()
        ? (x === x.toLowerCase() ? 1 : -1)
        : x.toLowerCase().localeCompare(y.toLowerCase())
      )).join("");
      cells.push(g);
    }
  }
  const counts = cells.reduce((m, g) => (m[g] = (m[g] || 0) + 1, m), {});

  // Phenotype ratio — dominant if any uppercase; recessive if all lowercase.
  const dominant = cells.filter((g) => /[A-Z]/.test(g)).length;
  const recessive = 4 - dominant;

  const gridX = el.x + 60;
  const gridY = el.y + 70;
  const cellSize = Math.min((el.width - 100) / 3, (el.height - 130) / 3);

  const cycle = (allele) => {
    // Cycle A → a → A per click
    return allele === allele.toUpperCase() ? allele.toLowerCase() : allele.toUpperCase();
  };
  const setP = (whichParent, whichIdx) => {
    if (whichParent === 1) {
      const arr = [p1[0], p1[1]];
      arr[whichIdx] = cycle(arr[whichIdx]);
      onStateChange({ p1: arr.join("") });
    } else {
      const arr = [p2[0], p2[1]];
      arr[whichIdx] = cycle(arr[whichIdx]);
      onStateChange({ p2: arr.join("") });
    }
  };

  return (
    <>
      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={8} fill="#FAF5FF" stroke="#7C3AED" strokeWidth={2} />
      <text x={el.x + 12} y={el.y + 22} fontSize={12} fontWeight={700} fill="#0A0C10">Punnett square</text>
      <text x={el.x + 12} y={el.y + 38} fontSize={11} fill="#7C3AED">tap parent letters to flip dominant/recessive</text>

      {/* Parent 1 (top labels) */}
      {a1.map((al, i) => (
        <g key={"p1" + i} onMouseDown={(e) => { e.stopPropagation(); setP(1, i); }} style={{ cursor: "pointer" }}>
          <rect x={gridX + (i + 1) * cellSize} y={gridY} width={cellSize} height={cellSize} fill="#FDE68A" stroke="#0A0C10" strokeWidth={1.5} />
          <text x={gridX + (i + 1) * cellSize + cellSize / 2} y={gridY + cellSize / 2 + 8} textAnchor="middle" fontSize={24} fontWeight={700} fill="#0A0C10">{al}</text>
        </g>
      ))}

      {/* Parent 2 (left labels) */}
      {a2.map((al, i) => (
        <g key={"p2" + i} onMouseDown={(e) => { e.stopPropagation(); setP(2, i); }} style={{ cursor: "pointer" }}>
          <rect x={gridX} y={gridY + (i + 1) * cellSize} width={cellSize} height={cellSize} fill="#FDE68A" stroke="#0A0C10" strokeWidth={1.5} />
          <text x={gridX + cellSize / 2} y={gridY + (i + 1) * cellSize + cellSize / 2 + 8} textAnchor="middle" fontSize={24} fontWeight={700} fill="#0A0C10">{al}</text>
        </g>
      ))}

      {/* Corner */}
      <rect x={gridX} y={gridY} width={cellSize} height={cellSize} fill="#FFFFFF" stroke="#0A0C10" strokeWidth={1.5} />
      <text x={gridX + cellSize / 2} y={gridY + cellSize / 2 + 4} textAnchor="middle" fontSize={10} fill="#94A3B8">P₁ × P₂</text>

      {/* Offspring cells */}
      {cells.map((g, i) => {
        const c = i % 2, r = Math.floor(i / 2);
        const isDominant = /[A-Z]/.test(g);
        return (
          <g key={"cell" + i}>
            <rect x={gridX + (c + 1) * cellSize} y={gridY + (r + 1) * cellSize} width={cellSize} height={cellSize}
                  fill={isDominant ? "#DBEAFE" : "#FEE2E2"} stroke="#0A0C10" strokeWidth={1.5} />
            <text x={gridX + (c + 1) * cellSize + cellSize / 2} y={gridY + (r + 1) * cellSize + cellSize / 2 + 8} textAnchor="middle"
                  fontSize={22} fontWeight={700} fill="#0A0C10">{g}</text>
          </g>
        );
      })}

      {/* Ratio read-out */}
      <text x={el.x + 12} y={el.y + el.height - 18} fontSize={11} fontWeight={700} fill="#0A0C10">
        Genotype: {Object.entries(counts).map(([g, n]) => `${n} ${g}`).join(" · ")}
      </text>
      <text x={el.x + 12} y={el.y + el.height - 4} fontSize={11} fill="#7C3AED">
        Phenotype: {dominant}:{recessive} dominant:recessive
      </text>
    </>
  );
}


// ─────────────── Skill Widget (interactive MCQ on the canvas) ───────────────
/** SvgSkillWidget — renders a step-by-step practice widget directly on the
 * whiteboard. Uses <foreignObject> so we can host normal HTML/CSS controls.
 *
 * Expected el.state shape:
 *   {
 *     skillId, title, topic, difficulty,
 *     questions: [{ prompt, options: [], answer_index, explanation? }],
 *     idx, answers: [...], picked, revealed, done, startedAt
 *   }
 *
 * The widget mutates its own state via onStateChange (persisted with the
 * scene). When the last question is answered, the score card is shown and an
 * attempt is silently POSTed to /api/skills/{id}/attempt so teachers can see
 * live progress in the Analytics dashboards.
 */
function SvgSkillWidget({ el, onStateChange, onDelete }) {
  const [regenerating, setRegenerating] = React.useState(false);
  const s = el.state || {};
  const questions = s.questions || [];
  const idx = s.idx ?? 0;
  const q = questions[idx];
  const total = questions.length;
  const picked = s.picked ?? null;
  const revealed = !!s.revealed;
  const done = !!s.done;
  const answers = s.answers || [];
  const correctIdx = q?.answer_index ?? 0;
  const score = answers.filter((a) => a.correct).length;

  // Live reporting — students in a live class push every pick to the teacher's
  // "Live responses" panel (works for enrolled students AND invite-link guests).
  const reportLive = React.useCallback((liveAnswers, currentIdx, isDone) => {
    const ctx = window.__liveSkillCtx;
    if (!ctx || ctx.role === "teacher" || !ctx.classId) return;
    const answersMap = {};
    liveAnswers.forEach((a) => { answersMap[a.qIdx] = a.picked; });
    const payload = {
      quiz_id: `skill:${el.id}`,
      answers: answersMap,
      score: liveAnswers.filter((a) => a.correct).length,
      total,
      time_s: Math.round((Date.now() - (s.startedAt || Date.now())) / 1000),
      current_q: currentIdx,
      status: isDone ? "final" : "partial",
    };
    import("@/lib/api").then((mod) => {
      const api = mod.default;
      if (ctx.guest?.token) {
        api.post(`/public/class/${ctx.classId}/quiz-response`, { ...payload, token: ctx.guest.token, guest_name: ctx.guest.name }).catch(() => {});
      } else {
        api.post(`/live/${ctx.classId}/quiz-response`, payload).catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el.id, total, s.startedAt]);

  // Fire an attempt once when we transition to `done`.
  const submittedRef = React.useRef(false);
  React.useEffect(() => {
    if (!done || submittedRef.current || !s.skillId) return;
    submittedRef.current = true;
    const duration = Math.round((Date.now() - (s.startedAt || Date.now())) / 1000);
    // Fire-and-forget — the canvas already reflects the score locally.
    import("@/lib/api").then((mod) => {
      const api = mod.default;
      api.post(`/skills/${s.skillId}/attempt`, {
        answers, score, total, duration_s: duration,
      }).catch(() => {});
    });
  }, [done]);

  const pick = (k) => {
    if (revealed || done) return;
    onStateChange({ picked: k, revealed: true });
    // Report the tentative pick immediately so the teacher sees the selection live.
    reportLive([...answers, { qIdx: idx, picked: k, correct: k === correctIdx }], idx, false);
  };

  const next = () => {
    const wasCorrect = picked === correctIdx;
    const nextAnswers = [...answers, { qIdx: idx, picked, correct: wasCorrect }];
    if (idx + 1 >= total) {
      onStateChange({ answers: nextAnswers, done: true, revealed: false, picked: null });
      reportLive(nextAnswers, idx, true);
    } else {
      onStateChange({ idx: idx + 1, answers: nextAnswers, revealed: false, picked: null });
      reportLive(nextAnswers, idx + 1, false);
    }
  };

  const retry = () => {
    onStateChange({ idx: 0, answers: [], picked: null, revealed: false, done: false, startedAt: Date.now() });
    submittedRef.current = false;
  };

  const regenerate = async () => {
    if (regenerating || !s.subject || !s.grade || !s.topic) return;
    setRegenerating(true);
    try {
      const mod = await import("@/lib/api");
      const api = mod.default;
      const { data } = await api.post("/skills/curriculum/practice", {
        subject: s.subject, grade: s.grade, topic: s.topic, count: total || 5, force: true,
      });
      const skill = data?.skill;
      const newQs = skill?.payload?.questions || [];
      if (newQs.length) {
        onStateChange({
          skillId: skill.id, questions: newQs,
          idx: 0, answers: [], picked: null, revealed: false, done: false,
          startedAt: Date.now(),
        });
        submittedRef.current = false;
      }
    } catch {
      /* swallow — user can retry */
    } finally {
      setRegenerating(false);
    }
  };

  const finalPct = total > 0 ? Math.round((score / total) * 100) : 0;
  const pillTint =
    (s.difficulty || "").toLowerCase() === "hard" ? "#DC2626" :
    (s.difficulty || "").toLowerCase() === "medium" ? "#F59E0B" : "#22C55E";

  return (
    <>
      <foreignObject x={el.x} y={el.y} width={el.width} height={el.height}>
        {(() => {
          const BASE_W = 520;
          const scale = Math.max(0.6, el.width / BASE_W);
          const innerW = BASE_W;
          const innerH = Math.max(300, el.height / scale);
          return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
        <div
          onWheel={(e) => e.stopPropagation()}
          style={{
            width: innerW, height: innerH,
            transform: `scale(${scale})`, transformOrigin: "top left",
            display: "flex", flexDirection: "column",
            fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
            color: "#0A0C10", overflow: "hidden",
            cursor: "grab",
            background: "white",
            border: "2px solid #7C3AED",
            borderRadius: 12,
            boxSizing: "border-box",
          }}
          data-testid={`skill-widget-${el.id}`}
        >
          {/* Header — click-and-drag surface; buttons below stop propagation */}
          <div style={{ padding: "10px 14px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", color: "white", background: "#7C3AED", borderRadius: "10px 10px 0 0" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, letterSpacing: "0.16em", opacity: 0.85, textTransform: "uppercase" }}>
                Skill · {s.topic || "Practice"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {s.title || "Skill"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {s.difficulty && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: pillTint, color: "#0A0C10", textTransform: "uppercase" }}>
                  {s.difficulty}
                </span>
              )}
              <span style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", padding: "3px 8px", borderRadius: 4, background: "rgba(255,255,255,0.18)" }}>
                {done ? `${score}/${total}` : `Q${idx + 1}/${total}`}
              </span>
              {(s.subject && s.grade && s.topic) && (
                <button
                  type="button"
                  disabled={regenerating}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); regenerate(); }}
                  data-testid={`skill-widget-regen-${el.id}`}
                  title="Regenerate this practice with fresh questions & visuals"
                  style={{
                    width: 22, height: 22, padding: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    border: 0, borderRadius: 999,
                    background: regenerating ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.14)",
                    color: "white", fontSize: 12, fontWeight: 700, lineHeight: 1,
                    cursor: regenerating ? "wait" : "pointer",
                  }}
                >{regenerating ? "…" : "↻"}</button>
              )}
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onDelete && onDelete(); }}
                data-testid={`skill-widget-close-${el.id}`}
                title="Remove this skill from the board"
                style={{
                  width: 22, height: 22, padding: 0, marginLeft: 2,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  border: 0, borderRadius: 999,
                  background: "rgba(255,255,255,0.14)", color: "white",
                  fontSize: 15, fontWeight: 700, lineHeight: 1, cursor: "pointer",
                }}
              >×</button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 3, background: "rgba(124,58,237,0.15)", margin: "0 12px 8px" }}>
            <div style={{
              height: "100%",
              width: done ? "100%" : `${(idx / Math.max(1, total)) * 100}%`,
              background: "#7C3AED", transition: "width .3s",
            }} />
          </div>

          {/* Body */}
          <div style={{ flex: 1, padding: "4px 14px 8px", overflowY: "auto" }}>
            {done ? (
              <div style={{ textAlign: "center", paddingTop: 8 }}>
                <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, letterSpacing: "0.2em", color: "#7C3AED", textTransform: "uppercase" }}>
                  / your score
                </div>
                <div style={{ fontSize: 42, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.03em", marginTop: 6 }}>
                  {score} / {total}
                </div>
                <div style={{ fontSize: 13, color: "#4B5563", marginTop: 4 }}>
                  {finalPct}% correct
                  {finalPct === 100 ? " · Perfect run!" :
                    finalPct >= 80 ? " · Great work!" :
                    finalPct >= 50 ? " · Keep going!" : " · Try again"}
                </div>
                {/* Per-question review — what was answered, and the correct
                    answer whenever the pick was wrong. */}
                <div style={{ textAlign: "left", marginTop: 14, display: "grid", gap: 8 }} data-testid={`skill-review-${el.id}`}>
                  {questions.map((qq, i) => {
                    const a = answers.find((x) => x.qIdx === i);
                    const ok = !!a?.correct;
                    return (
                      <div key={i} style={{
                        background: ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.07)",
                        border: `1px solid ${ok ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.3)"}`,
                        borderRadius: 8, padding: "8px 10px",
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", display: "flex", gap: 6 }}>
                          <span style={{ color: ok ? "#059669" : "#DC2626" }}>{ok ? "✓" : "✗"}</span>
                          <span>Q{i + 1}. {qq.prompt}</span>
                        </div>
                        <div style={{ fontSize: 11.5, marginTop: 3, color: ok ? "#059669" : "#DC2626" }}>
                          Your answer: {qq.options?.[a?.picked] ?? "Not answered"}
                        </div>
                        {!ok && (
                          <div style={{ fontSize: 11.5, marginTop: 2, color: "#059669", fontWeight: 700 }}>
                            Correct answer: {qq.options?.[qq.answer_index]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); retry(); }}
                  data-testid={`skill-widget-retry-${el.id}`}
                  style={{
                    marginTop: 14, padding: "8px 16px", fontSize: 12, fontWeight: 700,
                    background: "#0A0C10", color: "#FFD600", border: 0, borderRadius: 6,
                    cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase",
                  }}
                >
                  Try again
                </button>
              </div>
            ) : q ? (
              <>
                {q.visual_svg && (
                  <div
                    style={{
                      background: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      padding: 6,
                      marginTop: 4,
                      marginBottom: 8,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      maxHeight: 140,
                      overflow: "hidden",
                    }}
                    data-testid={`skill-widget-visual-${el.id}`}
                    className="skill-visual-slot"
                    dangerouslySetInnerHTML={{ __html: q.visual_svg }}
                  />
                )}
                <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, marginTop: 4 }}>
                  {q.prompt}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginTop: 10 }}>
                  {(q.options || []).map((opt, k) => {
                    const isPicked = picked === k;
                    const isCorrect = k === correctIdx;
                    let bg = "white", border = "1px solid #E5E7EB", color = "#0A0C10";
                    if (revealed) {
                      if (isCorrect) { bg = "#DCFCE7"; border = "1px solid #22C55E"; }
                      else if (isPicked) { bg = "#FEE2E2"; border = "1px solid #EF4444"; }
                    } else if (isPicked) { bg = "#EDE9FE"; border = "1px solid #7C3AED"; }
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={revealed}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); pick(k); }}
                        data-testid={`skill-widget-opt-${el.id}-${k}`}
                        style={{
                          textAlign: "left", padding: "8px 10px", fontSize: 12, fontWeight: 500,
                          background: bg, border, borderRadius: 6, color, cursor: revealed ? "default" : "pointer",
                          display: "flex", alignItems: "center", gap: 8,
                        }}
                      >
                        <span style={{
                          width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center",
                          borderRadius: 4, background: "#0A0C10", color: "#FFD600", fontSize: 10, fontWeight: 800, flexShrink: 0,
                        }}>
                          {String.fromCharCode(65 + k)}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{opt}</span>
                      </button>
                    );
                  })}
                </div>

                {revealed && (
                  <div style={{
                    marginTop: 10, padding: "8px 10px", borderRadius: 6, fontSize: 11, lineHeight: 1.4,
                    background: picked === correctIdx ? "#F0FDF4" : "#FEF2F2",
                    border: `1px solid ${picked === correctIdx ? "#22C55E" : "#EF4444"}`,
                    color: "#0A0C10",
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: q?.explanation ? 3 : 0 }}>
                      {picked === correctIdx ? "Correct!" : `Answer: ${String.fromCharCode(65 + correctIdx)}`}
                    </div>
                    {q?.explanation && <div style={{ color: "#4B5563" }}>{q.explanation}</div>}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: 20, textAlign: "center", color: "#6B7280", fontSize: 12 }}>
                No questions available for this skill.
              </div>
            )}
          </div>

          {/* Footer / Next button */}
          {!done && q && (
            <div style={{ padding: "6px 14px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #F3F4F6" }}>
              <div style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", color: "#7C3AED", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                score · {score}
              </div>
              <button
                type="button"
                disabled={!revealed}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid={`skill-widget-next-${el.id}`}
                style={{
                  padding: "6px 14px", fontSize: 11, fontWeight: 700,
                  background: revealed ? "#7C3AED" : "#E5E7EB",
                  color: revealed ? "white" : "#9CA3AF",
                  border: 0, borderRadius: 6, cursor: revealed ? "pointer" : "not-allowed",
                  letterSpacing: "0.05em", textTransform: "uppercase",
                }}
              >
                {idx + 1 >= total ? "Finish" : "Next"}
              </button>
            </div>
          )}
        </div>
        </div>
          );
        })()}
      </foreignObject>
    </>
  );
}

